import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  Alert,
} from "@mui/material";
import EmptyRows from "../../../../components/reusableComponent/EmptyRows";
import TableModal from "../../../../components/reusableComponent/TableModal";
import { useGetTicketClickToExpandQuery } from "../../../../Services/dashboard/TicketDashboard";

const MAX_SELECTED_DISPOSITIONS = 5;
const DISPOSITION_LIMIT_ERROR = `You can select up to ${MAX_SELECTED_DISPOSITIONS} dispositions only. Please remove one to select another.`;

const MIN_CHART_HEIGHT = 270;

const DISPOSITION_MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 280,
      overflowY: "auto" as const,
    },
  },
};

interface IPROPS {
  getDispositionData: any;
  startDate?: string;
  endDate?: string;
  teamId?: string | null;
  workflowId?: string | null;
}

const Disposition: React.FC<IPROPS> = ({
  getDispositionData,
  startDate,
  endDate,
  teamId,
  workflowId,
}) => {
  const navigate = useNavigate();
  const [selectedCount, setSelectedCount] = useState<number>(5);
  const [selectedDispositions, setSelectedDispositions] = useState<string[]>([]);
  const [dispositionSelectError, setDispositionSelectError] = useState("");
  const [chartData, setChartData] = useState<any[]>([]);
  
  // Get default top 5 dispositions for pre-selection
  const defaultTopDispositions = useMemo(() => {
    const allDispositions = getDispositionData?.all_dispositions || getDispositionData?.top_five_dispositions || [];
    return allDispositions.slice(0, selectedCount).map((item: any) => item.disposition_name);
  }, [getDispositionData, selectedCount]);
  
  const totalDispositionCount = chartData.reduce((total, item) => total + item.value, 0);

  const COLORS = [
    "#10b981", // green
    "#f59e0b", // amber
    "#ef4444", // red
    "#6366f1", // indigo (Ticket Information primary)
    "#8b5cf6", // purple
  ];

  const [activeIndex, setActiveIndex] = useState<number | undefined>();
  const [allZero, setAllZero] = useState(false);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [selectedDisposition, setSelectedDisposition] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [clickToExpandPage, setClickToExpandPage] = useState(1);

  // ── Click-to-expand query ─────────────────────────────────────────────────
  // value = disposition id (as string)
  const clickToExpandParams =
    tableModalOpen && selectedDisposition && teamId && startDate && endDate
      ? {
          graph_name: "top_five_dispositions",
          value: selectedDisposition.id,
          start_date: startDate,
          end_date: endDate,
          team_id: teamId,
          page: clickToExpandPage,
          page_size: 10,
          workflow_id: workflowId || null,
        }
      : null;

  const { data: clickToExpandData, isLoading: isClickToExpandLoading } =
    useGetTicketClickToExpandQuery(
      clickToExpandParams ?? {
        graph_name: "",
        value: "",
        start_date: "",
        end_date: "",
        team_id: "",
      },
      { skip: !clickToExpandParams },
    );

  const clickToExpandRows = useMemo(() => {
    if (!clickToExpandData) return [];
    const raw =
      (clickToExpandData as any)?.results ??
      (clickToExpandData as any)?.data ??
      clickToExpandData;
    return Array.isArray(raw) ? raw : [];
  }, [clickToExpandData]);
const getRandomColor = (index: number) => {
    const hue = (index * 137.508) % 360; // Golden angle approximation for good distribution
    const saturation = 70 + (index % 20); // Vary saturation slightly
    const lightness = 45 + (index % 15); // Vary lightness slightly
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };
  const clickToExpandPagination = useMemo(() => {
    const d = clickToExpandData as any;
    if (!d) return null;
    const count = d.count ?? (Array.isArray(d.results) ? d.results.length : 0);
    const totalPages = d.total_pages ?? 1;
    const page = d.page ?? clickToExpandPage;
    const pageSize = d.page_size ?? 15;
    return { totalCount: count, currentPage: page, totalPages, pageSize };
  }, [clickToExpandData, clickToExpandPage]);

  useEffect(() => {
    if (tableModalOpen) setClickToExpandPage(1);
  }, [selectedDisposition?.id]);

  // Initialize selected dispositions with default top 5 when data changes
  useEffect(() => {
    if (defaultTopDispositions.length > 0 && selectedDispositions.length === 0) {
      setSelectedDispositions(defaultTopDispositions);
    }
  }, [defaultTopDispositions]);

  useEffect(() => {
    // Get all available dispositions (either from all_dispositions or fallback to top_five_dispositions)
    const allDispositions = getDispositionData?.all_dispositions || getDispositionData?.top_five_dispositions || [];
    
    // If no dispositions available or all are deselected, show empty state
    if (!allDispositions.length || selectedDispositions.length === 0) {
      setAllZero(true);
      setChartData([]);
      return;
    }

    // Show selected dispositions
    const dispositionsToShow = allDispositions.filter((item: any) => 
      selectedDispositions.includes(item.disposition_name)
    );

    const normalized = dispositionsToShow.map((item: any, index: number) => ({
      name: item.disposition_name,
      value: Number(item.count ?? 0),
      fill:  getRandomColor(index),
      disposition_id: item.disposition_id ?? item.id,
    }));

    setAllZero(normalized.every(d => d.value === 0));
    setActiveIndex(undefined);
    setChartData(normalized);
  }, [getDispositionData, selectedCount, selectedDispositions]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleRowClick = (dispositionInfo: any) => {
    if (!teamId || !startDate || !endDate) return;
    // value = disposition id (number → string)
    const id = String(
      dispositionInfo?.disposition_id ?? dispositionInfo?.id ?? "",
    );
    if (!id) return;
    setSelectedDisposition({
      id,
      name: dispositionInfo?.disposition_name ?? id,
    });
    setTableModalOpen(true);
    setClickToExpandPage(1);
  };

  const handleSegmentClick = (index: number) => {
    if (!teamId || !startDate || !endDate) return;
    const disposition = chartData[index];
    if (disposition) {
      const id = String(disposition.disposition_id);
      setSelectedDisposition({
        id,
        name: disposition.name,
      });
      setTableModalOpen(true);
      setClickToExpandPage(1);
    }
  };

  const handleCloseModal = () => {
    setTableModalOpen(false);
    setSelectedDisposition(null);
    setClickToExpandPage(1);
  };

  const dispositionOptions =
    getDispositionData?.all_dispositions ||
    getDispositionData?.top_five_dispositions ||
    [];

  const showDispositionLimitError = () => {
    setDispositionSelectError(DISPOSITION_LIMIT_ERROR);
  };

  const handleDispositionChange = (e: SelectChangeEvent<string[]>) => {
    const value =
      typeof e.target.value === "string"
        ? e.target.value.split(",")
        : e.target.value;

    if (value.length > MAX_SELECTED_DISPOSITIONS) {
      showDispositionLimitError();
      return;
    }

    setDispositionSelectError("");
    setSelectedDispositions(value);
  };

  const CustomLegend = () => {
    const total = chartData.reduce((sum, item) => sum + item.value, 0);
    
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {chartData.map((item, index) => {
          const percentage = total > 0 ? ((item.value / total) * 100).toFixed(0) : 0;
          const isActive = activeIndex === index;
          
          return (
            <div
              key={item.name}
              role="button"
              onClick={() => handleSegmentClick(index)}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(undefined)}
              style={{
                padding: "8px 12px",
                borderRadius: 6,
                cursor: teamId && startDate && endDate ? "pointer" : "default",
                background: isActive ? `${item.fill}10` : "#f8fafc",
                border: `1.5px solid ${isActive ? item.fill : 'transparent'}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                transition: "all 0.2s ease",
                transform: isActive ? "scale(1.02)" : "scale(1)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    background: item.fill,
                    boxShadow: isActive ? `0 0 8px ${item.fill}80` : 'none',
                  }}
                />
                <span style={{ 
                  color: "#374151", 
                  fontWeight: isActive ? "600" : "500",
                  fontSize: "13px",
                  marginRight: "5px"
                }}>
                  {item.name}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ 
                  color: "#9ca3af", 
                  fontSize: "12px",
                  fontWeight: "500"
                }}>
                  {percentage}%
                </span>
                <strong style={{ 
                  color: isActive ? item.fill : "#1f2937", 
                  fontSize: "14px",
                  minWidth: "30px",
                  textAlign: "right"
                }}>
                  {item.value}
                </strong>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0];
    const total = payload[0].payload.value;

    return (
      <div
        style={{
          background: "white",
          padding: "8px 12px",
          borderRadius: 6,
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          border: `2px solid ${data.payload.fill}`,
        }}
      >
        <div style={{ 
          color: data.payload.fill, 
          fontWeight: "600",
          fontSize: "13px",
          marginBottom: 2
        }}>
          {data.name}
        </div>
        <div style={{ color: "#6b7280", fontSize: "12px" }}>
          {total} tickets
        </div>
      </div>
    );
  };

  const clickable = !!(teamId && startDate && endDate);
  const ticketDashboardDisposition = "disposition";

  const PieChartContainer = PieChart as any;
  const PieContainer = Pie as any;
  const CellContainer = Cell as any;
  const ResponsiveChartContainer = ResponsiveContainer as any;
  const LegendContainer = Legend as any;
  const TooltipContainer = Tooltip as any;

  const chartAreaHeight = useMemo(() => {
    const count = chartData.length;
    if (count === 0) return MIN_CHART_HEIGHT;
    const legendHeight = count * 44 + (count - 1) * 6;
    return Math.max(MIN_CHART_HEIGHT, legendHeight);
  }, [chartData.length]);

  return (
    <div className="card card-flush h-lg-100">
      {/* ── Header ── */}
      <div className="card-header pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="fs-2 fw-bold text-gray-800 me-2 lh-1 ls-n2">
            Top {selectedDispositions.length === 0 ? "5" : selectedDispositions.length}{" "}
            dispositions : {totalDispositionCount}
          </span>
        </h3>
        <div className="card-toolbar">
          <FormControl
            sx={{ minWidth: 180, maxWidth: 220 }}
            error={!!dispositionSelectError}
          >
            <InputLabel id="ticket-disposition-multi-select-label">Select Dispositions</InputLabel>
            <Select
              labelId="ticket-disposition-multi-select-label"
              id="ticket-disposition-multi-select"
              multiple
              value={selectedDispositions}
              onChange={handleDispositionChange}
              MenuProps={DISPOSITION_MENU_PROPS}
              input={<OutlinedInput label="Select Dispositions" />}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return `Top ${selectedCount}`;
                }
                return selected.join(", ");
              }}
            >
              {dispositionOptions.map((disposition: any) => {
                const isSelected =
                  selectedDispositions.indexOf(disposition.disposition_name) > -1;
                const atMax =
                  selectedDispositions.length >= MAX_SELECTED_DISPOSITIONS;

                return (
                  <MenuItem
                    key={disposition.disposition_name}
                    value={disposition.disposition_name}
                    onMouseDown={(e) => {
                      if (atMax && !isSelected) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    onClick={(e) => {
                      if (atMax && !isSelected) {
                        e.preventDefault();
                        e.stopPropagation();
                        showDispositionLimitError();
                      }
                    }}
                    sx={
                      atMax && !isSelected
                        ? { opacity: 0.55 }
                        : undefined
                    }
                  >
                    <Checkbox checked={isSelected} />
                    <ListItemText primary={disposition.disposition_name} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
      </div>
      {dispositionSelectError && (
        <div className="px-5 pb-0">
          <Alert severity="error">
            {dispositionSelectError}
          </Alert>
        </div>
      )}

      {/* ── Body ── */}
      <div className="card-body py-0">
        {allZero ? (
          <div
            style={{
              height: MIN_CHART_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EmptyRows value={ticketDashboardDisposition} />
          </div>
        ) : (
          <ResponsiveChartContainer width="100%" height={chartAreaHeight}>
            <PieChartContainer>
              <PieContainer
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius="45%"
                outerRadius="75%"
                dataKey="value"
                onClick={(_: any, index: number) => handleSegmentClick(index)}
                onMouseEnter={(_: any, index: number) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(undefined)}
                paddingAngle={3}
                animationBegin={0}
                animationDuration={600}
              >
                {chartData.map((d, i) => (
                  <CellContainer
                    key={i}
                    fill={d.fill}
                    strokeWidth={activeIndex === i ? 4 : 2}
                    stroke={activeIndex === i ? "white" : "#ffffff"}
                    style={{
                      filter: activeIndex === i
                        ? `drop-shadow(0 4px 12px ${d.fill}60)`
                        : "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                      transition: "all 0.3s ease",
                      transform: activeIndex === i ? "scale(1.05)" : "scale(1)",
                      transformOrigin: "center",
                    }}
                  />
                ))}
              </PieContainer>

              <TooltipContainer content={<CustomTooltip />} />
              <LegendContainer
                content={<CustomLegend />}
                layout="vertical"
                align="right"
                verticalAlign="middle"
                wrapperStyle={{ paddingLeft: 20 }}
              />
            </PieChartContainer>
          </ResponsiveChartContainer>
        )}
      </div>

      {/* ── Click-to-expand modal ── */}
      <TableModal
        isOpen={tableModalOpen}
        onRequestClose={handleCloseModal}
        title={
          selectedDisposition
            ? `Tickets – ${selectedDisposition.name}`
            : "Tickets"
        }
        contentLabel="Disposition tickets click-to-expand"
        emptyMessage="No tickets found for this disposition."
        data={clickToExpandRows}
        isLoading={isClickToExpandLoading}
        getRowKey={(row: any, idx: number) => row?.ticket_id ?? row?.id ?? idx}
        maxWidth="1200px"
        onRowClick={(row: any) => {
          const ticketId = row?.ticket_id ?? row?.id;
          if (ticketId != null) {
            handleCloseModal();
            navigate(`/ticket/information/${ticketId}`);
          }
        }}
        totalCount={clickToExpandPagination?.totalCount}
        currentPage={clickToExpandPagination?.currentPage}
        totalPages={clickToExpandPagination?.totalPages}
        pageSize={clickToExpandPagination?.pageSize}
        onPageChange={setClickToExpandPage}
        columns={[
          {
            label: "Ticket ID",
            render: (row: any) => row?.ticket_id ?? row?.id ?? "—",
          },
          { label: "Title", render: (row: any) => row?.title ?? "—" },
          { label: "Status Type", render: (row: any) => row?.status_type ?? "—" },
          {
            label: "Contact Name",
            render: (row: any) => row?.contact_name ?? "—",
          },
          {
            label: "Contact Email",
            render: (row: any) => row?.contact_email ?? "—",
          },
          {
            label: "Contact Phone",
            render: (row: any) => row?.contact_phone ?? "—",
          },
          {
            label: "Created",
            render: (row: any) => {
              const raw = row?.created;
              if (!raw) return "—";
              const d = new Date(raw);
              if (Number.isNaN(d.getTime())) return "—";
              const dd = String(d.getDate()).padStart(2, "0");
              const mm = String(d.getMonth() + 1).padStart(2, "0");
              const yyyy = d.getFullYear();
              const hh = String(d.getHours()).padStart(2, "0");
              const min = String(d.getMinutes()).padStart(2, "0");
              const ss = String(d.getSeconds()).padStart(2, "0");
              return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
            },
          },
        ]}
      />
    </div>
  );
};

export default Disposition;